import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { puoOperare } from "@/lib/ruoli";
import {
  Package, Users, Truck, AlertTriangle, ShieldCheck, FileText, Wrench,
  MapPin, Building2, UserX, ClipboardList, ExternalLink, Clock,
  ChevronLeft, ChevronRight, RefreshCw, PhoneCall,
} from "lucide-react";
import NewReportDialog from "@/components/NewReportDialog";
import CodiceVisita from "@/components/CodiceVisita";
import DashboardFleetMap from "@/components/DashboardFleetMap";

const REFRESH_MS = 60000;
const WEEKDAY = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const shiftIso = (iso, days) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtGiorno = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return `${WEEKDAY[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [todaySites, setTodaySites] = useState(null);
  const [staffing, setStaffing] = useState(null);
  const [reperibili, setReperibili] = useState(null);
  const [staffDate, setStaffDate] = useState(todayIso());
  const [showReport, setShowReport] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // L'amministrativo vede il quadro di chi gestisce, non quello dell'ospite.
  const isTecnico = puoOperare(user);
  // Il magazziniere non entra nei fascicoli del personale: qui comparirebbero
  // nomi e documenti delle persone, e il server glieli nega comunque.
  const vedeFascicoli = user?.role !== "operator";
  const nav = useNavigate();

  const loadStaffing = useCallback((date) => {
    api.get(`/dashboard/staffing?date=${date}`).then(({ data }) => setStaffing(data)).catch(() => {});
    api.get("/reperibilita/adesso").then(({ data }) => setReperibili(data)).catch(() => {});
  }, []);

  // Cantieri e impegni cambiano durante la giornata: la dashboard si riallinea
  // da sola e al rientro sulla scheda, senza dover ricaricare la pagina.
  const staffDateRef = React.useRef(staffDate);
  staffDateRef.current = staffDate;

  const refresh = useCallback(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    if (vedeFascicoli) {
      api.get("/dashboard/expiring?days=90").then(({ data }) => setExpiring(data)).catch(() => {});
    }
    if (isTecnico) {
      api.get("/dashboard/today-sites").then(({ data }) => setTodaySites(data)).catch(() => {});
      loadStaffing(staffDateRef.current);
    }
  }, [isTecnico, vedeFascicoli, loadStaffing]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (isTecnico) loadStaffing(staffDate); }, [isTecnico, loadStaffing, staffDate]);

  useEffect(() => {
    if (!isTecnico) return undefined;
    const timer = setInterval(refresh, REFRESH_MS);
    const onFocus = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [isTecnico, refresh]);

  if (user?.role === "preposto") return <PrepostoDashboard />;

  if (!stats) {
    return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
    </div>;
  }

  const t = stats.totals;
  const scaduti = (expiring?.items || []).filter(c => c.status === "expired");
  const inScadenza30 = (expiring?.items || []).filter(c => c.status === "expiring_30");
  const inScadenza90 = (expiring?.items || []).filter(c => c.status === "expiring_90");
  const totalWarnings = scaduti.length + inScadenza30.length + inScadenza90.length;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Panoramica</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Benvenuto, {user?.name}</p>
        </div>
        <Button onClick={() => setShowReport(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="dash-new-report-btn">
          <ClipboardList className="w-4 h-4 mr-2" /> Nuova segnalazione
        </Button>
      </div>
      {showReport && <NewReportDialog onClose={() => setShowReport(false)} onSaved={() => setShowReport(false)} />}

      {isTecnico && (
        <>
          {/* RIGA 1: Localizzazione mezzi e cantieri della giornata */}
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display font-bold flex items-center gap-2">
                  <Truck className="w-4 h-4 text-orange-500" /> Localizzazione mezzi e cantieri di oggi
                </h2>
                <Link to="/localizzazione" className="text-xs text-orange-600 hover:underline flex items-center gap-1">
                  Vista completa <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <DashboardFleetMap sites={todaySites || []} />
            </CardContent>
          </Card>

          {/* RIGA 2: Cantieri di oggi + Disponibilità del personale.
              Altezza fissa condivisa: le due card restano allineate e scorrono
              ciascuna al proprio interno invece di allungare la pagina. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:h-[26rem]">
            <Card className="border-slate-200 h-full flex flex-col overflow-hidden">
              <CardContent className="p-5 flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-display font-bold flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-orange-500" /> Cantieri attivi oggi
                    {todaySites?.length > 0 && (
                      <span className="text-xs font-normal text-slate-400">· {todaySites.length}</span>
                    )}
                  </h2>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={refresh} title="Aggiorna"
                            className="text-slate-400 hover:text-orange-600" data-testid="dash-refresh-sites">
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                    <Link to="/cantieri" className="text-xs text-orange-600 hover:underline">Tutti i cantieri</Link>
                  </div>
                </div>
                {!todaySites ? <p className="text-sm text-slate-400">Caricamento...</p> : todaySites.length === 0 ? (
                  <p className="text-sm text-slate-500">Nessun cantiere con date impostate per oggi.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-1.5 content-start flex-1 min-h-0 overflow-y-auto pr-1">
                    {todaySites.map(s => (
                      <Link key={s.id} to={`/cantieri/${s.id}`}
                            className="block px-2 py-1.5 border border-slate-200 rounded hover:border-orange-300 hover:bg-orange-50/40 transition">
                        <div className="flex items-baseline justify-between gap-1.5">
                          <span className="font-semibold text-[13px] leading-tight truncate">{s.name}</span>
                          <span className="text-[10px] text-slate-400 flex-shrink-0 whitespace-nowrap">
                            {s.employees_count}<Users className="w-2.5 h-2.5 inline-block mx-0.5 -mt-0.5" />
                            {s.equipment_count}<Truck className="w-2.5 h-2.5 inline-block ml-0.5 -mt-0.5" />
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1 truncate leading-tight">
                          <MapPin className="w-2.5 h-2.5 flex-shrink-0" />{s.address}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <StaffingCard staffing={staffing} date={staffDate}
                          onDate={setStaffDate} onToday={() => setStaffDate(todayIso())} />
          </div>

          {/* Chi risponde al telefono adesso: di notte e' l'unica cosa che serve. */}
          <ReperibiliCard dati={reperibili} />
        </>
      )}

      {/* RIGA 3: Attestati da controllare + Scorte basse. Al magazziniere
          resta solo la seconda, che occupa tutta la riga. */}
      <div className={`grid grid-cols-1 gap-4 ${vedeFascicoli ? "lg:grid-cols-2" : ""}`}>
        {vedeFascicoli && (
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" /> Attestati da controllare
              </h2>
              {isAdmin && <Link to="/discrepanze" className="text-xs text-orange-600 hover:underline">Vedi tutto</Link>}
            </div>
            {!expiring ? <p className="text-sm text-slate-400">Caricamento...</p> : (
              totalWarnings === 0 ? (
                <p className="text-sm text-emerald-700 flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Tutto in regola nei prossimi 90 giorni.</p>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {scaduti.slice(0, 5).map(c => (
                    <div key={c.cert_id} className="flex items-center justify-between text-sm p-1.5 bg-red-50 rounded">
                      <span className="truncate">{c.employee_name} · {c.category}</span>
                      <Badge variant="destructive" className="text-[10px] flex-shrink-0">scaduto</Badge>
                    </div>
                  ))}
                  {inScadenza30.slice(0, Math.max(0, 5 - scaduti.length)).map(c => (
                    <div key={c.cert_id} className="flex items-center justify-between text-sm p-1.5 bg-amber-50 rounded">
                      <span className="truncate">{c.employee_name} · {c.category}</span>
                      <Badge className="bg-amber-500 text-white border-0 text-[10px] flex-shrink-0">{c.days_remaining}gg</Badge>
                    </div>
                  ))}
                  {inScadenza90.slice(0, Math.max(0, 5 - scaduti.length - inScadenza30.length)).map(c => (
                    <div key={c.cert_id} className="flex items-center justify-between text-sm p-1.5 bg-yellow-50 rounded">
                      <span className="truncate">{c.employee_name} · {c.category}</span>
                      <Badge className="bg-yellow-200 text-yellow-800 border-0 text-[10px] flex-shrink-0">{c.days_remaining}gg</Badge>
                    </div>
                  ))}
                  {totalWarnings > 5 && (
                    <p className="text-xs text-slate-400 pt-1">+ altri {totalWarnings - 5}</p>
                  )}
                </div>
              )
            )}
          </CardContent>
        </Card>
        )}

        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold flex items-center gap-2">
                <Package className="w-4 h-4 text-red-500" /> Scorte basse magazzino DPI
              </h2>
              <Link to="/magazzino" className="text-xs text-orange-600 hover:underline">Vai al magazzino</Link>
            </div>
            {stats.low_stock.length === 0 ? (
              <p className="text-sm text-emerald-700 flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Nessun avviso, scorte sopra soglia.</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {stats.low_stock.slice(0, 6).map(l => (
                  <div key={`${l.item_id}-${l.size}`} className="flex items-center justify-between text-sm p-1.5 bg-red-50 rounded">
                    <span className="truncate">{l.name} · taglia {l.size}</span>
                    <Badge variant="destructive" className="text-[10px] flex-shrink-0">{l.qty}/{l.min_stock}</Badge>
                  </div>
                ))}
                {stats.low_stock.length > 6 && <p className="text-xs text-slate-400 pt-1">+ altri {stats.low_stock.length - 6}</p>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* RIGA 4: Numeri generali, più in basso */}
      <div>
        <h2 className="font-display text-lg font-bold mb-2 text-slate-600">Numeri generali</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatMini icon={Users} label="Dipendenti" value={t.employees} to="/dipendenti" />
          <StatMini icon={Package} label="Articoli DPI" value={t.items} to="/magazzino" />
          <StatMini icon={Truck} label="Consegne" value={t.deliveries} to="/storico" />
          <StatMini icon={FileText} label="Attestati" value={t.certificates} to="/dipendenti" />
          <StatMini icon={Wrench} label="Mezzi/Attrezzature" value="" to="/attrezzature" />
        </div>
      </div>

    </div>
  );
}

function ReperibiliCard({ dati }) {
  if (!dati) return null;
  // I monoperatori escono in piu' d'uno, ognuno sulla sua postazione: stanno in
  // "in_turno_multipli", non in "in_turno". Guardando solo "in_turno" il
  // riquadro diceva sempre "nessuno assegnato" anche con la settimana coperta.
  // E una settimana senza monoperatori non e' un buco: il servizio si attiva su
  // richiesta del committente, quindi non deve far scattare l'allarme rosso.
  const scoperto = dati.ruoli.some(r => !r.multiplo && !r.in_turno);
  return (
    <Card className="border-slate-200" data-testid="dash-reperibili">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-orange-500" /> Reperibili adesso
            {dati.giorno_di_cambio && (
              <span className="text-[11px] font-normal text-amber-700">
                cambio oggi alle {dati.ora_cambio}
              </span>
            )}
          </h2>
          <Link to="/reperibilita" className="text-xs text-orange-600 hover:underline flex items-center gap-1">
            Calendario <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {dati.ruoli.map(r => {
            const mono = r.multiplo ? (r.in_turno_multipli || []) : null;
            const vuoto = r.multiplo ? false : !r.in_turno;
            return (
            <div key={r.codice} className={`rounded-lg border p-3 ${
              vuoto ? "border-red-200 bg-red-50" : "border-slate-200"}`}
                 data-testid={`dash-rep-${r.codice}`}>
              <div className="text-[11px] text-slate-400">{r.nome}</div>
              {mono ? (
                mono.length === 0 ? (
                  <div className="text-sm text-slate-500 mt-1">nessuno in servizio</div>
                ) : (
                  <div className="space-y-1.5 mt-1">
                    {mono.map((v, i) => (
                      <div key={i} className="border-b border-slate-100 last:border-0 pb-1 last:pb-0">
                        <a href={`tel:${(v.postazione?.telefono || "").replace(/\s/g, "")}`}
                           className="text-sm font-semibold text-[#0067b2]">
                          {v.postazione?.telefono || "—"}
                          <span className="text-[10px] text-slate-400 font-normal ml-1.5">
                            {v.postazione?.nome}
                          </span>
                        </a>
                        <div className="text-sm font-medium">{v.full_name}</div>
                      </div>
                    ))}
                  </div>
                )
              ) : (<>
                <a href={`tel:${(r.telefono_turno || "").replace(/\s/g, "")}`}
                   className="text-base font-semibold text-[#0067b2]">
                  {r.telefono_turno || "—"}
                </a>
                <div className="text-sm">
                  {r.in_turno
                    ? <span className="font-medium">{r.in_turno.full_name}</span>
                    : <span className="text-red-600 text-xs">nessuno assegnato</span>}
                </div>
              </>)}
              {r.monta && (
                <div className="text-[11px] text-slate-500 mt-1">
                  poi <strong>{r.monta.full_name}</strong>
                </div>
              )}
            </div>
            );
          })}
        </div>
        {scoperto && (
          <p className="text-xs text-red-700 mt-3">
            Un turno senza nome vuol dire un telefono che squilla a vuoto.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function StaffingCard({ staffing, date, onDate, onToday }) {
  const isToday = date === todayIso();
  return (
    <Card className="border-slate-200 h-full flex flex-col overflow-hidden">
      <CardContent className="p-5 flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="font-display font-bold flex items-center gap-2">
            <UserX className="w-4 h-4 text-slate-500" /> Personale libero
          </h2>
          <Link to="/pianificazione-griglia" className="text-xs text-orange-600 hover:underline">Pianifica</Link>
        </div>

        {/* Frecce per sbirciare domani e i giorni successivi prima di assegnare. */}
        <div className="flex items-center justify-between gap-2 mb-3 bg-slate-50 rounded px-2 py-1.5">
          <button type="button" onClick={() => onDate(shiftIso(date, -1))}
                  className="p-1 rounded hover:bg-white text-slate-500" data-testid="staffing-prev-day">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-center leading-tight">
            <div className="text-sm font-semibold capitalize">{fmtGiorno(date)}</div>
            {!isToday && (
              <button type="button" onClick={onToday} className="text-[10px] text-orange-600 hover:underline">
                torna a oggi
              </button>
            )}
          </div>
          <button type="button" onClick={() => onDate(shiftIso(date, 1))}
                  className="p-1 rounded hover:bg-white text-slate-500" data-testid="staffing-next-day">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {!staffing ? <p className="text-sm text-slate-400">Caricamento...</p> : (
          <div className="space-y-3 flex-1 min-h-0 overflow-y-auto pr-1">
            <StaffingGroup
              title="Liberi" count={staffing.unassigned_count} tone="text-slate-600"
              empty="Nessun dipendente libero in questa giornata."
              people={staffing.unassigned}
              render={(e) => (
                <Badge key={e.id} variant="outline" className="text-[11px] font-normal">{e.full_name}</Badge>
              )} />
            <StaffingGroup
              title="In ferie o permesso" count={staffing.on_leave_count ?? 0} tone="text-amber-700"
              people={staffing.on_leave}
              render={(e) => (
                <Badge key={e.id} className="bg-amber-100 text-amber-700 border-0 text-[11px] font-normal">
                  {e.full_name} · {e.leave_type}
                </Badge>
              )} />
            <StaffingGroup
              title="Impegnati" count={staffing.assigned_count} tone="text-emerald-700"
              people={staffing.assigned || []}
              render={(e) => (
                <Badge key={e.id} className="bg-emerald-50 text-emerald-700 border-0 text-[11px] font-normal"
                       title={e.site_name}>
                  {e.full_name}
                </Badge>
              )} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StaffingGroup({ title, count, tone, people, render, empty }) {
  if (!people?.length && !empty) return null;
  return (
    <div>
      <div className={`text-[11px] uppercase tracking-widest font-semibold mb-1.5 ${tone}`}>
        {title} · {count}
      </div>
      {people?.length ? (
        <div className="flex flex-wrap gap-1.5">{people.map(render)}</div>
      ) : (
        <p className="text-xs text-slate-400">{empty}</p>
      )}
    </div>
  );
}

function StatMini({ icon: Icon, label, value, to }) {
  return (
    <Link to={to}>
      <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer h-full">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
            <Icon className="w-4 h-4 text-slate-400" />
          </div>
          <div className="font-display text-2xl font-black text-slate-900">{value}</div>
        </CardContent>
      </Card>
    </Link>
  );
}

function PrepostoDashboard() {
  const [sites, setSites] = React.useState(null);
  React.useEffect(() => {
    api.get("/sites").then(({ data }) => setSites(data)).catch(() => setSites([]));
  }, []);

  if (sites === null) return <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Panoramica</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">I tuoi cantieri</h1>
      </div>
      {sites.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-10 text-center">
            <Wrench className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold text-slate-700">Nessun cantiere assegnato</h3>
            <p className="text-sm text-slate-500 mt-1">Quando un tecnico ti assegnerà a un cantiere, lo troverai qui.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map(s => (
            <Card key={s.id} className="border-slate-200 hover:shadow-md transition-all h-full">
              <CardContent className="p-5 flex flex-col h-full">
                <Link to={`/cantieri/${s.id}`} className="block">
                  <div className="font-display font-bold text-lg">{s.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.address}</div>
                  <Badge className="mt-3">{s.status}</Badge>
                </Link>
                {/* Il coordinatore o l'ispettore che arriva in cantiere chiede
                    il codice a chi ci lavora: il pulsante sta qui perche' e'
                    qui che il preposto guarda quando arriva qualcuno. */}
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <CodiceVisita cantiereId={s.id} cantiereNome={s.name} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
